const parseDate = (isoDate) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const formatUtc = (date, options) =>
  date.toLocaleDateString("en-US", { timeZone: "UTC", ...options });

export const addDays = (isoDate, days) => {
  const date = parseDate(isoDate);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

// The business day a shift is drawn on: a show's shifts use the show's date; a shift with no show
// that starts before 5am belongs to the night before, as the presenter's day columns do.
export const shiftDay = (shift) =>
  !shift.calendar_event_id && shift.start_clock < "05:00"
    ? addDays(shift.work_date, -1)
    : shift.work_date;

// Shifts not tied to a show sit in their day's "Not tied to a show" column.
export const shiftColumnId = (shift) =>
  shift.calendar_event_id ?? `day-${shiftDay(shift)}`;

export const clockLabel = (clock) => {
  if (!clock) return "";
  const [hours, minutes] = clock.split(":").map(Number);
  const suffix = hours < 12 ? "a" : "p";
  const hour = hours % 12 === 0 ? 12 : hours % 12;
  return minutes === 0
    ? `${hour}${suffix}`
    : `${hour}:${String(minutes).padStart(2, "0")}${suffix}`;
};

export const columnDayLabel = (isoDate) =>
  formatUtc(parseDate(isoDate), {
    weekday: "short",
    month: "short",
    day: "numeric",
  })
    .replace(",", " ·")
    .toUpperCase();

export const drawerDayLabel = (isoDate) =>
  formatUtc(parseDate(isoDate), {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).replace(",", " ·");

export const weekLabel = (startsOn, endsOn) => {
  const start = parseDate(startsOn);
  const end = parseDate(endsOn);
  const endYear = end.getUTCFullYear();
  if (start.getUTCFullYear() !== endYear) {
    return `${formatUtc(start, { month: "short", day: "numeric", year: "numeric" })} – ${formatUtc(end, { month: "short", day: "numeric", year: "numeric" })}`;
  }
  if (start.getUTCMonth() !== end.getUTCMonth()) {
    return `${formatUtc(start, { month: "short", day: "numeric" })} – ${formatUtc(end, { month: "short", day: "numeric" })}, ${endYear}`;
  }
  return `${formatUtc(start, { month: "short", day: "numeric" })} – ${end.getUTCDate()}, ${endYear}`;
};

export const hoursLabel = (minutes) =>
  String(Math.round((minutes / 60) * 10) / 10);

export const moneyLabel = (amount) => {
  const value = Number(amount);
  const fractionDigits = Number.isInteger(value) ? 0 : 2;
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

export const wholeDollars = (amount) =>
  `$${Math.round(Number(amount)).toLocaleString("en-US")}`;

export const percentLabel = (percent) =>
  percent == null ? "—" : `${Number(percent).toLocaleString("en-US")}%`;

export const rateLabel = (rate) =>
  `$${Number(rate).toLocaleString("en-US", { maximumFractionDigits: 2 })}/h`;

export const shortName = (firstName, lastName) =>
  lastName ? `${firstName} ${lastName[0]}.` : firstName;

export const shiftMinutes = (startClock, endClock, unpaidBreakMinutes) => {
  if (!startClock || !endClock) return 0;
  const toMinutes = (clock) =>
    clock
      .split(":")
      .map(Number)
      .reduce((hours, minutes) => hours * 60 + minutes);
  let length = toMinutes(endClock) - toMinutes(startClock);
  if (length <= 0) length += 24 * 60;
  return Math.max(length - Number(unpaidBreakMinutes || 0), 0);
};

// Minutes into the business day, which runs 05:00 to 05:00 (Shift::NEXT_DAY_START_BEFORE),
// so a 00:30 start lands after the evening instead of before it.
export const businessMinutes = (clock) => {
  const [hours, minutes] = clock.split(":").map(Number);
  return (clock < "05:00" ? hours + 24 : hours) * 60 + minutes;
};

export const minutesClock = (minutes) => {
  const wrapped = minutes % (24 * 60);
  return `${String(Math.floor(wrapped / 60)).padStart(2, "0")}:${String(wrapped % 60).padStart(2, "0")}`;
};
